import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { matchPath, useLocation } from "react-router-dom";

import { IRoutePattern } from "../@types/Route";
import { routesWithChildRoutes } from "../routes/routes";

type IChildRoutesContextData = {
  childRoutes: Array<IRoutePattern>;
  hideChildRoute: (name: string) => void;
  showChildRoute: (name: string) => void;
};

type IChildRoutesProvider = {
  children: ReactNode;
};

export const ChildRoutesContext = createContext({} as IChildRoutesContextData);

export function ChildRoutesProvider({ children }: IChildRoutesProvider) {
  // EN-5199: the set of hidden route names is the ONLY state; the visible menu
  // is derived from the base list. Hide/show are idempotent and
  // order-independent, so feature gates firing in the same commit (e.g. the
  // Measurements/Labs/Pinned effects on PatientPage) cannot overwrite each
  // other the way the previous "rebuild the whole list and setState" approach
  // did.
  const [hiddenRouteNames, setHiddenRouteNames] = useState<Set<string>>(
    () => new Set()
  );
  const location = useLocation();

  const hideChildRoute = useCallback((name: string) => {
    setHiddenRouteNames((prevHidden) => {
      if (prevHidden.has(name)) {
        return prevHidden;
      }

      const updatedHidden = new Set(prevHidden);
      updatedHidden.add(name);
      return updatedHidden;
    });
  }, []);

  const showChildRoute = useCallback((name: string) => {
    setHiddenRouteNames((prevHidden) => {
      if (!prevHidden.has(name)) {
        return prevHidden;
      }

      const updatedHidden = new Set(prevHidden);
      updatedHidden.delete(name);
      return updatedHidden;
    });
  }, []);

  const valuesToShare = useMemo(() => {
    /**
     * This piece of code will match the routes that have the same
     * child routes.
     */
    const activeChildRoutes =
      routesWithChildRoutes.find(({ paths }) => {
        return paths.find((path) =>
          matchPath(
            {
              path,
            },
            location.pathname
          )
        );
      })?.childRoutes || [];

    return {
      childRoutes: activeChildRoutes.filter(
        (childRoute) => !hiddenRouteNames.has(childRoute.name)
      ),
      hideChildRoute,
      showChildRoute,
    };
  }, [location, hiddenRouteNames, hideChildRoute, showChildRoute]);

  return (
    <ChildRoutesContext.Provider value={valuesToShare}>
      {children}
    </ChildRoutesContext.Provider>
  );
}
